"use client";

import useIsMobile from "@packages/hooks/DetectMobileDevice";
import API_URL from "@packages/lib/server-actions/api-urls";
import makeApiCall from "@packages/lib/server-actions/rest-api";
import {
  clickstreamForClicks,
  gaForCtaClicks,
  getProjectSpecificData,
} from "@packages/lib/utlils/commonFunction";
import React, { useEffect, useState } from "react";
import ButtonLoader from "./ButtonLoader";
import { ClickstreamInterface } from "@packages/lib/utlils/clickstream";
import { ClickstreamConstants } from "@packages/shared-components/services/constants";
import { CallNowButtonProps } from "@packages/lib/types/revenue-buttons/buttons-interface";

const CTA_LABEL = "Call now";

const CallNowButton = ({
  enquiryProps,
  commonGAAttrs,
  btnSize = "min-w-[160px] w-full",
  btnName,
}: CallNowButtonProps) => {
  const [ctaLabel, setCtaLabel] = useState<any>(CTA_LABEL);
  const [enquiryDetails, setEnquiryDetails] = useState<any | null>(null);
  const [isMobile] = useIsMobile();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  let csData: ClickstreamInterface = {} as ClickstreamInterface;
  csData.CTATitle = ClickstreamConstants.callNowCTA;
  csData.actionType = ClickstreamConstants.clickAction;

  csData.eventType = enquiryProps?.eventType
    ? enquiryProps?.eventType
    : "ClearingHotlineClicked";
  csData.functionalityName = enquiryProps?.functionalityName
    ? enquiryProps?.functionalityName
    : "Clearing";

  useEffect(() => {
    if (
      ctaLabel === CTA_LABEL &&
      (enquiryDetails?.stdHotlineNumber || enquiryDetails?.hotline)
    )
      setCtaLabel(() =>
        enquiryProps?.subOrderItemId
          ? enquiryDetails?.hotline
          : enquiryDetails?.stdHotlineNumber
      );
  }, [enquiryDetails]);

  const logGaClickstream = (hotlineNumber: string) => {
    clickstreamForClicks(enquiryProps, csData, commonGAAttrs, "", true);
    gaForCtaClicks(
      enquiryProps,
      "Hotline",
      { ...commonGAAttrs },
      "",
      ""
    );
  };

  const getEnquiryDetails = async () => {
    setIsLoading(true);
    try {
      const payload = {
        suborderItemId: enquiryProps?.subOrderItemId,
        orderItemId: enquiryProps?.orderItemId,
        collegeId: enquiryProps?.collegeId,
        courseId: enquiryProps?.courseId,
        qualCode: enquiryProps?.qualCode,
        affiliateId: getProjectSpecificData("affiliateId"),
      };

      const response = await makeApiCall({
        url: `${process.env.NEXT_PUBLIC_DOMSERVICE_API_DOMAIN}/${API_URL?.getEnquiryDetails}`,
        method: "POST",
        body: payload,
        headers: {
          "x-api-key": `${process.env.NEXT_PUBLIC_DOMSERVICE_X_API_KEY}`,
        },
      });
      if (response) {
        logGaClickstream(
          enquiryProps?.subOrderItemId
            ? response?.hotline
            : response?.stdHotlineNumber
        );
        setEnquiryDetails(() => response);
      }
    } catch (error) {
      console.error("Error while fetching enquiry details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openDialPad = (hotlineNumber: string | number) => {
    window.location.href = `tel:${hotlineNumber}`;
  };

  const handleCallNow = async (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    if (enquiryProps?.hotlineNumber && ctaLabel === CTA_LABEL) {
      setCtaLabel(enquiryProps?.hotlineNumber);
      logGaClickstream(enquiryProps?.hotlineNumber);
    } else if (
      !enquiryProps?.hotlineNumber &&
      !enquiryProps?.stdHotlineNumber &&
      !enquiryDetails
    )
      await getEnquiryDetails();

    if (
      !enquiryProps?.hotlineNumber &&
      !enquiryDetails?.stdHotlineNumber &&
      !enquiryDetails?.hotline
    )
      return;

    if (isMobile && ctaLabel && ctaLabel !== CTA_LABEL)
      openDialPad(String(ctaLabel));
  };

  return (
    <button
      type="button"
      className={`btn btn-negative-default flex justify-center items-center gap-[8px] ${btnSize}`}
      onClick={handleCallNow}
    >
      <ButtonLoader isLoading={isLoading}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.612793 1.66632C0.612793 1.23683 0.960956 0.888672 1.39044 0.888672H3.0646C3.44475 0.888672 3.76917 1.1635 3.83167 1.53847L4.40659 4.988C4.46271 5.32474 4.29265 5.65872 3.9873 5.81139L2.7834 6.41334C3.65148 8.57048 5.37328 10.2923 7.53042 11.1604L8.13237 9.95646C8.28505 9.65111 8.61902 9.48105 8.95576 9.53717L12.4053 10.1121C12.7803 10.1746 13.0551 10.499 13.0551 10.8792V12.5533C13.0551 12.9828 12.7069 13.331 12.2774 13.331H10.7222C5.13891 13.331 0.612793 8.80485 0.612793 3.2216V1.66632Z"
            fill="white"
          />
        </svg>
        {btnName || ctaLabel}
      </ButtonLoader>
    </button>
  );
};

export default CallNowButton;
